import { useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import {
    faEllipsisH, faShoppingBasket, faPlus, faCubes, faBell, faBoxesStacked, faBarcode,
} from '@fortawesome/free-solid-svg-icons';
import EntityTable from '../Listing/EntityTable';
import ProductQuickView from '../Product/ProductQuickView';
import {
    productIdColumn, productNameColumn, productPriceColumn, productStockColumn,
    productStatusColumn, productRefundableColumn, productBarcodeColumn,
} from '../Product/productColumns';
import InputText from '../Form/Fields/InputText';
import InputSliderRange from '../Form/Fields/InputSliderRange';
import useRangeMax from '../../hooks/useRangeMax';
import Select from '../Form/Fields/Select';
import { getProductStatusOptions } from '../../config/ProductStatus';
import { getYesNoOptions } from '../../config/YesNo';
import { useProductActions } from '../../hooks/useProductActions';
import { useAuth } from '../../context/AuthProvider';
import { API_BASE_URL } from '../../api';

const LaboratoryProducts = ({ laboratoryId }) => {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    const [quickViewProduct, setQuickViewProduct] = useState(null);
    const priceMax = useRangeMax(`${API_BASE_URL}/api/v1/products`, 'publicPrice', { params: { laboratoryId } });
    const { settings, hasRight } = useAuth();
    const canEdit = hasRight('PRODUCTS_EDIT');
    const globalStockAlertThreshold = Number(settings?.stock_inventory_min_stock_alert) || 10;

    const { addToCart, addingToCartId, handleAddStock, handleViewStock, openQuickEdit, modals } =
        useProductActions({ onRefresh: () => tableRef.current?.reload() });

    // Data cells shared with the products listing (productColumns.js); filters and the actions
    // menu below are this screen's own.
    const columns = [
        { ...productIdColumn(), filterable: { component: InputText } },
        // "Par <labo>" would repeat this laboratory on every row
        { ...productNameColumn({ t, showLaboratory: false }), filterable: { component: InputText } },
        {
            ...productPriceColumn({ t, onEditPrice: canEdit ? (row) => openQuickEdit(row.id, 'ppv', 'PPV (prix public)', row.publicPrice) : undefined }),
            filterable: { component: InputSliderRange, label: 'Prix', min: 0, max: priceMax, step: 1, unit: 'dhs' },
        },
        {
            ...productStockColumn({ t, alertThreshold: globalStockAlertThreshold }),
            // fixed scale, same as the Products page's stock filter ("200+" at the end)
            filterable: { component: InputSliderRange, min: 0, max: 200, step: 1 },
        },
        { ...productStatusColumn({ t }), filterable: { component: Select, options: getProductStatusOptions(), label: 'Statut' } },
        { ...productRefundableColumn({ t }), filterable: { component: Select, options: getYesNoOptions(), label: 'Remboursable' } },
        { ...productBarcodeColumn({ t }), filterable: { component: InputText } },
        {
            Header: '',
            accessor: 'actions',
            className: 'col-actions',
            Cell: (row) => (
                <div className="cell-dropdown" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    <button
                        className="cell-actions-btn"
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Ajouter au panier"
                        onClick={() => addToCart(row.id)}
                        disabled={addingToCartId === row.id}
                    >
                        {addingToCartId === row.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <FontAwesomeIcon icon={faShoppingBasket} />}
                    </button>
                    <button
                        className="cell-actions-btn"
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Voir les lots"
                        onClick={() => handleViewStock(row.id, row.name)}
                    >
                        <FontAwesomeIcon icon={faCubes} />
                    </button>
                    <button
                        className="cell-actions-btn dropdown-toggle"
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Plus d'actions"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button className="dropdown-item" onClick={() => addToCart(row.id)}>
                                <FontAwesomeIcon icon={faShoppingBasket} className="me-2 text-muted" />
                                Ajouter au panier
                            </button>
                        </li>
                        {canEdit && <li><hr className="dropdown-divider" /></li>}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => handleAddStock(row.id)}>
                                    <FontAwesomeIcon icon={faPlus} className="me-2 text-muted" />
                                    Alimenter le stock
                                </button>
                            </li>
                        )}
                        <li>
                            <button className="dropdown-item" onClick={() => handleViewStock(row.id, row.name)}>
                                <FontAwesomeIcon icon={faCubes} className="me-2 text-muted" />
                                Voir les lots
                            </button>
                        </li>
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'minimumStock', "Seuil d'alerte stock", row.minimumStock > 0 ? row.minimumStock : globalStockAlertThreshold)}>
                                    <FontAwesomeIcon icon={faBell} className="me-2 text-muted" />
                                    Seuil d'alerte stock
                                </button>
                            </li>
                        )}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'maximumStock', 'Seuil de stock max', row.maximumStock > 0 ? row.maximumStock : '')}>
                                    <FontAwesomeIcon icon={faBoxesStacked} className="me-2 text-muted" />
                                    Seuil de stock max
                                </button>
                            </li>
                        )}
                        <li><hr className="dropdown-divider" /></li>
                        <li>
                            <NavLink to={`/products/${row.id}`} className="dropdown-item">
                                Voir le médicament
                            </NavLink>
                        </li>
                        {canEdit && <li><hr className="dropdown-divider" /></li>}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'barcode', 'Code-barres', row.barcode)}>
                                    <FontAwesomeIcon icon={faBarcode} className="me-2 text-muted" />
                                    Modifier le code-barres
                                </button>
                            </li>
                        )}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'ppv', 'PPV (prix public)', row.publicPrice)}>
                                    Modifier le prix PPV
                                </button>
                            </li>
                        )}
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openQuickEdit(row.id, 'pph', 'PPH (prix hospitalier)', row.hospitalPrice)}>
                                    Modifier le prix PPH
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            ),
        },
    ];

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/products`}
                columns={columns}
                defaultSort="stockCount"
                defaultDirection="desc"
                pageSize={20}
                emptyMessage="Aucun produit"
                searchable={true}
                multiSelect={true}
                searchPlaceholder="Rechercher un médicament ..."
                extraParams={{ laboratoryId, prefixMatch: true }}
                onRowClick={(row) => setQuickViewProduct(row)}
                getRowDragData={(row) => ({ type: 'product', id: row.id, name: row.name })}
            />
            {quickViewProduct && (
                <ProductQuickView
                    product={quickViewProduct}
                    onClose={() => setQuickViewProduct(null)}
                    onAddToCart={addToCart}
                    addingToCart={addingToCartId === quickViewProduct.id}
                    onAddStock={handleAddStock}
                    onViewStock={handleViewStock}
                />
            )}
            {modals}
        </>
    );
};

export default LaboratoryProducts;
